import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import FoundationLayout from "./components/FoundationLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import GuestOnlyRoute from "./components/GuestOnlyRoute";
import AdminLayout from "./admin/components/Layout";
import SEOHead from "./components/SEOHead";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import VerifyEmail from "./pages/VerifyEmail";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import BusinessOnboarding from "./pages/BusinessOnboarding";
import BusinessProfile from "./pages/BusinessProfile";
import AccountProfile from "./pages/AccountProfile";
import FoundationDashboard from "./pages/FoundationDashboard";
import Analytics from "./pages/Analytics";
import WorkspacePlaceholder from "./pages/WorkspacePlaceholder";
import ContentCalendar from "./pages/ContentCalendar";
import AIPostStudio from "./pages/AIPostStudio";
import AIPostCreate from "./pages/AIPostCreate";
import AIPostEditor from "./pages/AIPostEditor";
import ManualPostStudio from "./pages/ManualPostStudio";
import ManualPostEditor from "./pages/ManualPostEditor";
import PostStudioRedirect from "./pages/PostStudioRedirect";
import MediaLibrary from "./pages/MediaLibrary";
import SocialAccounts from "./pages/SocialAccounts";
import Messages from "./pages/Messages";
import Leads from "./pages/Leads";
import LeadCapturePage from "./pages/LeadCapturePage";
import LeadDetails from "./pages/LeadDetails";
import LeadFormBuilder from "./pages/LeadFormBuilder";
import UpgradePlan from "./pages/UpgradePlan";
import Notifications from "./pages/Notifications";
import Settings from "./pages/Settings";
import HelpSupport from "./pages/HelpSupport";

import AdminDashboard from "./admin/pages/Dashboard";
import AdminSupportTickets from "./admin/pages/SupportTickets";
import AdminUsers from "./admin/pages/Users";
import { LegacyBusinessRedirect, LegacyUserRedirect } from "./admin/pages/LegacyUserRedirects";
import AdminIntegrations from "./admin/pages/Integrations";
import AdminSubscriptionManagement from "./admin/pages/SubscriptionManagement";
import AdminAIProviderSettings from "./admin/pages/AIProviderSettings";
import AdminAccessControl from "./admin/pages/AccessControl";
import AdminPaymentGateways from "./admin/pages/PaymentGateways";
import AdminSmtpSettings from "./admin/pages/SmtpSettings";
import AdminWebsiteBanners from "./admin/pages/website/WebsiteBanners";
import AdminContactSubmissions from "./admin/pages/ContactSubmissions";
import ToastNotifications from "./components/ToastNotifications";
import ImpersonationBanner from "./components/ImpersonationBanner";
// Loaded last so the shared design layer sits on top of every module stylesheet.
import "./styles/ui-system.css";

function App() {
  return (
    <ToastNotifications>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <SEOHead />
        <ImpersonationBanner />
        <Routes>

        <Route path="/lead/:token" element={<LeadCapturePage />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/resend-verification" element={<VerifyEmail mode="resend" />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />

        <Route element={<GuestOnlyRoute />}>
          <Route path="/" element={<Login />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
        </Route>

        <Route element={<ProtectedRoute requireOnboarding={false} />}>
          <Route path="/onboarding" element={<BusinessOnboarding />} />
        </Route>

        <Route element={<ProtectedRoute requireOnboarding />}>
          <Route path="/" element={<FoundationLayout />}>
            <Route path="dashboard" element={<FoundationDashboard />} />
            <Route path="content-calendar" element={<ContentCalendar />} />
            <Route path="ai-post-studio" element={<AIPostStudio />} />
            <Route path="ai-post-studio/create" element={<AIPostCreate />} />
            <Route path="ai-post-studio/:postId/edit" element={<AIPostEditor />} />
            <Route path="manual-post-studio" element={<ManualPostStudio />} />
            <Route path="manual-post-studio/create" element={<ManualPostEditor />} />
            <Route path="manual-post-studio/:postId/edit" element={<ManualPostEditor />} />
            {/* Former single post pages; kept as redirects for saved links and notifications. */}
            <Route path="create-content" element={<Navigate to="/ai-post-studio" replace />} />
            <Route path="posts" element={<PostStudioRedirect />} />
            <Route path="media-library" element={<MediaLibrary />} />
            <Route path="social-accounts" element={<SocialAccounts />} />
            <Route path="messages" element={<Messages />} />
            <Route path="leads" element={<Leads />} />
            <Route path="leads/forms/new" element={<LeadFormBuilder />} />
            <Route path="leads/forms/:formId/edit" element={<LeadFormBuilder />} />
            <Route path="leads/:leadId" element={<LeadDetails />} />
            <Route path="analytics" element={<Analytics />} />
            <Route path="business-profile" element={<BusinessProfile />} />
            <Route path="products-services" element={<WorkspacePlaceholder />} />
            <Route path="brand-settings" element={<WorkspacePlaceholder />} />
            <Route path="content-preferences" element={<WorkspacePlaceholder />} />
            <Route path="notifications" element={<Notifications />} />
            <Route path="help-support" element={<HelpSupport />} />
            <Route path="settings" element={<Settings />} />
            <Route path="upgrade-plan" element={<UpgradePlan />} />
            <Route path="account/profile" element={<AccountProfile />} />
          </Route>
        </Route>

        <Route element={<ProtectedRoute requiredRole="admin" />}>
          <Route path="/" element={<AdminLayout />}>
            <Route path="admin/dashboard" element={<AdminDashboard />} />
            <Route path="admin/users" element={<AdminUsers />} />
            {/* User details and business profiles now open in the Users page's Quick View; old links still work. */}
            <Route path="admin/users/:userId" element={<LegacyUserRedirect />} />
            <Route path="admin/businesses" element={<Navigate to="/admin/users" replace />} />
            <Route path="admin/businesses/:businessId" element={<LegacyBusinessRedirect />} />
            <Route path="admin/support-tickets" element={<AdminSupportTickets />} />
            <Route path="admin/integrations" element={<AdminIntegrations />} />
            <Route path="admin/subscriptions" element={<AdminSubscriptionManagement />} />
            <Route path="admin/ai-providers" element={<AdminAIProviderSettings />} />
            <Route path="admin/access-control" element={<AdminAccessControl />} />
            <Route path="admin/payment-gateways" element={<AdminPaymentGateways />} />
            <Route path="admin/smtp" element={<AdminSmtpSettings />} />
            <Route path="admin/website" element={<Navigate to="/admin/website/banners" replace />} />
            <Route path="admin/website/banners" element={<AdminWebsiteBanners />} />
            <Route path="admin/contact-submissions" element={<AdminContactSubmissions />} />
          </Route>
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />

        </Routes>
      </Router>
    </ToastNotifications>
  );
}

export default App;
